import React, { useEffect, useState } from 'react'

import {
  AlignItems,
  Body2,
  Box,
  Color,
  Display,
  Heading2,
  Visible,
  withCitrusTheme,
} from '@gousto-internal/citrus-react'
import { fetchRecipeFeedback } from '@library/user-feedback'
import { Loader } from '../Loader'
import { RatingsComplete } from '../RatingsComplete'
import { Feedback, OnSubmit, TrackEvent } from '../types'
import { formatFeedbackResponse, getAllQuestions, getAllRecipes } from '../formatters'
import { FeedbackGrid } from '../FeedbackGrid'
import { FeedbackCarousel } from '../FeedbackCarousel'
import { queueTrackingEvent } from '@library/tracking-integration'

const Container = withCitrusTheme(Box, (theme) => ({
  maxWidth: '1200px',
  margin: '0 auto',
  paddingTop: theme.spacing(8),
  paddingBottom: theme.spacing(8),
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(3),
}))

const Divider = withCitrusTheme(Box, (theme) => ({
  flex: 1,
  marginLeft: theme.spacing(2),
  height: '1px',
  width: 'auto',
  backgroundColor: theme.color(Color.NeutralGrey_100),
}))

type RecipeFeedbackProps = {
  loadUserRecipeRatings: () => void
}

export const RecipeFeedback = ({ loadUserRecipeRatings }: RecipeFeedbackProps) => {
  const [loading, setLoading] = useState(false)
  const [feedback, setFeedback] = useState<Feedback[]>([])

  useEffect(() => {
    setLoading(true)
    queueTrackingEvent<TrackEvent>({
      action: 'view_rate_screen',
    })
    fetchRecipeFeedback().then((response) => {
      setFeedback(formatFeedbackResponse(response))
      setLoading(false)
    })
  }, [])

  const onSubmit: OnSubmit = (orderId, recipeId) => {
    const updatedFeedback = feedback.map((item) =>
      item.id === orderId
        ? { ...item, recipes: item.recipes.filter((r) => r.id !== recipeId) }
        : item,
    )

    setFeedback(updatedFeedback)

    loadUserRecipeRatings()
  }

  const recipes = getAllRecipes(feedback)
  const hasFeedback = feedback.some((item) => item.recipes.length > 0)

  return (
    <Container>
      <Box display="flex" alignItems={AlignItems.Center} width="100%">
        <Heading2>Rate my recipes</Heading2>
        <Divider />
      </Box>
      <Body2>
        We want to make our menu the best it can be, and knowing what you think really helps.
      </Body2>
      {loading && <Loader />}
      {!loading &&
        (hasFeedback ? (
          <>
            <Visible display={[Display.None, Display.Block]}>
              <FeedbackGrid feedback={feedback} onSubmit={onSubmit} />
            </Visible>
            <Visible display={[Display.Block, Display.None]}>
              <FeedbackCarousel
                recipes={recipes}
                questions={getAllQuestions(feedback)}
                onSubmit={onSubmit}
              />
            </Visible>
          </>
        ) : (
          <RatingsComplete />
        ))}
    </Container>
  )
}
